import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'

import {
  ArtistUpdatePreSaleItemsMutation,
  ArtistUpdatePreSaleItemsMutationVariables,
  ConsolePreSaleDetailFragmentDoc,
  ConsolePreSaleDetailQuery,
  ConsolePreSaleDetailQueryVariables,
  ConsolePresaleItemSearchQuery,
  ConsolePresaleItemSearchQueryVariables,
  ConsolePreSaleItemSelectBoxFragmentDoc,
  ConsolePreSaleItemSelectedBoxFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { DateTime } from '@/core/entities'
import { dayjs } from '@/lib/dayjs'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router } = useRouter<{ preSaleId: string }>()
  const [updatedAt] = useState(DateTime.now())

  const { data, loading } = useQuery<
    ConsolePreSaleDetailQuery,
    ConsolePreSaleDetailQueryVariables
  >(QUERY, { variables: { id: router.query.preSaleId } })

  const [
    getConsoleItems,
    {
      data: consoleItems,
      loading: gettingConsoleItems,
      fetchMore: fetchMoreConsoleItems,
    },
  ] = useLazyQuery<
    ConsolePresaleItemSearchQuery,
    ConsolePresaleItemSearchQueryVariables
  >(CONSOLE_PRESALE_ITEM_SEARCH, { notifyOnNetworkStatusChange: true })

  const [artistUpdatePreSale, { loading: addingItem }] = useMutation<
    ArtistUpdatePreSaleItemsMutation,
    ArtistUpdatePreSaleItemsMutationVariables
  >(ARTIST_UPDATE_PRE_SALE_ITEMS)

  return {
    state: {
      loading,
      preSale: data?.artistPreSale ?? null,
      preSaleId: router.query.preSaleId,
      itemSelectProps: {
        loading: gettingConsoleItems,
        hasNextPage: !!consoleItems?.artistItems?.pageInfo.hasNextPage,
        items: consoleItems?.artistItems ?? null,
      },
      artistId: user?.uid ?? '',
      addingItem,
      removingPreSale: false,
      items: null,
      updatedAt: data?.artistPreSale ? updatedAt : '',
    },
    actions: {
      async onFetchMore() {
        if (!fetchMoreConsoleItems) {
          await getConsoleItems()
          return
        }
        await fetchMoreConsoleItems({
          variables: {
            pageInput: {
              cursor: consoleItems?.artistItems?.pageInfo.endCursor,
            },
          },
        })
      },
      async onRemove(itemId) {
        const preSale = data?.artistPreSale
        if (!preSale) return
        await artistUpdatePreSale({
          variables: {
            id: router.query.preSaleId,
            inputs: {
              title: preSale.title,
              description: preSale.description,
              endedAt: dayjs(preSale.endedAt).toISOString(),
              startedAt: dayjs(preSale.startedAt).toISOString(),
              itemIds: preSale.itemIds.filter(id => id !== itemId),
              note: preSale.note,
              isPublished: preSale.isPublished,
              paymentMethods: preSale.paymentMethods,
            },
          },
          optimisticResponse() {
            return {
              artistUpdatePreSale: {
                id: router.query.preSaleId,
                itemIds: preSale.itemIds.filter(id => id !== itemId),
                items: preSale.items.filter(item => item.id !== itemId),
                __typename: 'PreSale',
              },
            }
          },
        })
      },
      async onRemovePreSale() {
        //TODO add api remove preSale
      },
      async onSelect(itemIds) {
        const preSale = data?.artistPreSale
        if (!preSale) return
        await artistUpdatePreSale({
          variables: {
            id: router.query.preSaleId,
            inputs: {
              title: preSale.title,
              description: preSale.description,
              endedAt: dayjs(preSale.endedAt).toISOString(),
              startedAt: dayjs(preSale.startedAt).toISOString(),
              itemIds,
              note: preSale.note,
              isPublished: preSale.isPublished,
              paymentMethods: preSale.paymentMethods,
            },
          },
        })
      },
      async onSearch() {
        await getConsoleItems()
      },
    },
  }
})

const QUERY = gql`
  query ConsolePreSaleDetail($id: ID!) {
    artistPreSale(id: $id) {
      ...ConsolePreSaleDetail
      purchasedCartTransactionOrders {
        id
      }
    }
  }
  ${ConsolePreSaleDetailFragmentDoc}
`

const CONSOLE_PRESALE_ITEM_SEARCH = gql`
  query ConsolePresaleItemSearch(
    $filters: ArtistItemsFilterInput
    $pageInput: PageInput
  ) {
    artistItems(filters: $filters, pageInput: $pageInput) {
      edges {
        node {
          ...ConsolePreSaleItemSelectBox
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
  ${ConsolePreSaleItemSelectBoxFragmentDoc}
`

const ARTIST_UPDATE_PRE_SALE_ITEMS = gql`
  mutation ArtistUpdatePreSaleItems(
    $id: ID!
    $inputs: ArtistUpdatePreSaleInput!
  ) {
    artistUpdatePreSale(id: $id, inputs: $inputs) {
      id
      items {
        ...ConsolePreSaleItemSelectedBox
      }
      itemIds
    }
  }
  ${ConsolePreSaleItemSelectedBoxFragmentDoc}
`
